import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/react-hooks";
import { get, includes, xor } from "lodash";
import { ChevronDown } from "@sonder/ui/source/icons";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { Button } from "@sonder/ui/source/styled/fragments/Button";
import {
  FilterButtonList,
  FilterDropdownContent,
  FilterEmpty,
  FilterFooter,
  FilterTextArea,
} from "@sonder/ui/source/styled/fragments/GenericFilters";
import { Link } from "@sonder/ui/source/styled/fragments/Link";
import { useStore as useAuthStore } from "auth/store";
import {
  BuildingsData,
  CheckedBuildingsData,
  GetBuildingsResponse,
  GetBuildingsVariables,
  getBuildings,
} from "core/queries/GetBuildings";
import { setFilters } from "tasks/actions";
import { BuildingFilterGroupItem } from "tasks/components/Filters/BuildingFilterGroupItem";
import { useSegmentQueue } from "tasks/hooks";
import { useStore } from "tasks/store";

export const BuildingFilters = () => {
  const {
    state: { filters: globalFilters },
    dispatch,
  } = useStore();

  const { buildingIds } = globalFilters;
  const { state: authState } = useAuthStore();
  const [search, setSearch] = useState("");
  const pushToSegmentQueue = useSegmentQueue(globalFilters);
  const tomCityId = get(authState, "user.cityId", null);

  const onClickOption = (building: CheckedBuildingsData) => {
    const buildingId = building.id;
    const newBuildingIds = xor(buildingIds, [buildingId]);
    dispatch(setFilters({ buildingIds: newBuildingIds }));
    pushToSegmentQueue({
      eventName: "building_filters",
      action: "click",
      element: `building_filter-${buildingId}`,
      props: { nextValue: !building.isChecked ? "selected" : "deselected" },
      commonProps: {},
    });
  };

  const { data, error, loading } = useQuery<
    GetBuildingsResponse,
    GetBuildingsVariables
  >(getBuildings, {
    fetchPolicy: "cache-and-network",
    variables: {
      city: tomCityId,
    },
  });
  const buildings = useMemo(
    () =>
      get(data, "buildingConnections.buildings", [])
        .map((building: BuildingsData) => {
          const isChecked = includes(buildingIds, building.id);
          return {
            ...building,
            isChecked,
          };
        })
        .filter((building: CheckedBuildingsData) => {
          if (search === "") return true;

          return (
            building.isChecked ||
            building.name.toLowerCase().includes(search.toLowerCase()) ||
            building.internalRef.toLowerCase().includes(search.toLowerCase())
          );
        })
        .map((building: CheckedBuildingsData) => (
          <BuildingFilterGroupItem
            building={building}
            key={building.id}
            onClick={() => onClickOption(building)}
          />
        )),
    [loading, search, buildingIds],
  );
  const isEmpty = buildings.length === 0;
  const isEmptyAndSearched = isEmpty && search !== "";
  const totalSelected = (buildingIds && buildingIds.length) || 0;

  if ((loading && !data) || !!error) return null;

  return (
    <Dropdown
      trigger={
        <Button fullWidth iconRight type="button">
          Buildings
          {totalSelected > 0 && ` (${totalSelected})`}
          <ChevronDown />
        </Button>
      }
    >
      {({ isOpen: isPortalOpen }) =>
        isPortalOpen && (
          <FilterDropdownContent align="center">
            <FilterTextArea
              autoFocus
              disabled={loading || !!error}
              placeholder={`Search ${buildings.length} buildings`}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {isEmptyAndSearched && (
              <FilterEmpty>{`No Results for ${search}.`}</FilterEmpty>
            )}
            <FilterButtonList>{buildings}</FilterButtonList>
            <FilterFooter>
              {`${totalSelected || 0} Selected`}
              {totalSelected > 0 && (
                <Link
                  onClick={() => {
                    requestAnimationFrame(() =>
                      dispatch(setFilters({ buildingIds: [] })),
                    );
                  }}
                >
                  Clear selected
                </Link>
              )}
            </FilterFooter>
          </FilterDropdownContent>
        )
      }
    </Dropdown>
  );
};
